/**
 * Event detail modal. Reuses twentytwentysix listing modal shell so
 * Events behave like M List listings (overlay, close button, ESC, layout).
 */
import React, { useEffect } from "react";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function formatDateTime(ev) {
  const raw = ev.start_datetime || ev.start_date;
  if (!raw) return ev.pretty_datetime || null;
  try {
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return ev.pretty_datetime || raw;
    const month = MONTHS[d.getMonth()];
    const day = d.getDate();
    const time = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    return `${month} ${day}, ${time}`;
  } catch (_) {
    return ev.pretty_datetime || raw;
  }
}

export default function EventDetailModal({ event, onClose }) {
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  if (!event) return null;

  const name = event.name ?? "Event";
  const imageUrl = event.image || event.graphic_800 || event.graphic_400 || event.photo;
  const dateTime = formatDateTime(event);
  const location = event.location || null;
  const eventType = event.event_type || event.category || event.event_type_name || null;
  const seriesLabel = event.curated_series || event.series || null;
  const description = event.full_description || event.description || event.short_description || null;
  const ctaUrl = event.registration_url || event.url || null;
  const normalizedCtaUrl =
    ctaUrl && (ctaUrl.startsWith("http://") || ctaUrl.startsWith("https://")) ? ctaUrl : ctaUrl ? `https://${ctaUrl}` : null;
  const ctaLabel = event.cta_label || (normalizedCtaUrl ? "Register" : null);

  return (
    <div
      className="twentytwentysix-content__listing-detail-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="event-detail-title"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div className="twentytwentysix-content__listing-detail-modal" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="twentytwentysix-content__listing-detail-close"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>

        <div className="twentytwentysix-content__listing-detail-grid">
          <div className="twentytwentysix-content__listing-detail-content">
            {(eventType || seriesLabel) && (
              <p className="twentytwentysix-content__listing-detail-meta">
                {[eventType, seriesLabel].filter(Boolean).join(" · ")}
              </p>
            )}
            <h2 id="event-detail-title" className="twentytwentysix-content__listing-detail-title">
              {name}
            </h2>

            {(dateTime || location) && (
              <p className="twentytwentysix-content__listing-detail-subhead">
                {dateTime && <span>{dateTime}</span>}
                {dateTime && location && " · "}
                {location && <span>{location}</span>}
              </p>
            )}

            {description && (
              <div className="twentytwentysix-content__listing-detail-body">
                {typeof description === "string" ? <p>{description}</p> : description}
              </div>
            )}
          </div>

          <div className="twentytwentysix-content__listing-detail-image-wrap">
            {imageUrl ? (
              <img src={imageUrl} alt={name} className="twentytwentysix-content__listing-detail-image" />
            ) : (
              <div className="twentytwentysix-content__listing-detail-image-placeholder" aria-hidden="true" />
            )}
          </div>
        </div>

        <div className="twentytwentysix-content__listing-detail-actions">
          {normalizedCtaUrl && ctaLabel && (
            <a
              href={normalizedCtaUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="twentytwentysix-content__pill"
            >
              {ctaLabel}
            </a>
          )}
          <button
            type="button"
            className="twentytwentysix-content__pill twentytwentysix-content__pill--outline"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

